import { Component, Input, OnDestroy, OnInit } from '@angular/core';

import { AdvertisementType, GoogleAdTypes } from 'src/app/modules/aa-core/enums/advertisement-enum';
import { AppRouterDataService } from 'src/app/modules/app-routing/services/app-router-data/app-router-data.service';
import { fromEvent, interval, Subscription } from 'rxjs';
import { startWith, shareReplay, distinctUntilChanged, map, withLatestFrom, delay } from 'rxjs/operators';
import { GoogleAdsList, SideBarLeftAdditonalSizes, UnleashFlags } from 'src/app/core/models/constants';

import { FeatureFlagService } from 'src/app/shared/services/feature-flag.service';
import { AppService } from '../../services/app-service/app.service';
import { GoogleTagService } from '../../services/gpt.service';

@Component({
  selector: 'aa-app-advertisement',
  templateUrl: './app-advertisement.component.html',
  styleUrls: ['./app-advertisement.component.scss'],
  standalone: false
})
export class AppAdvertisementComponent implements OnInit, OnDestroy {
  @Input() type: AdvertisementType;
  @Input() isBetaFeatures = false;
  readonly types = AdvertisementType;
  adObj;
  adSlot;
  subs = new Subscription();
  private readonly visibilityChange$ = fromEvent(document, 'visibilitychange').pipe(
    startWith('visible'),
    shareReplay({ refCount: true, bufferSize: 1 })
  );
  private readonly isVisible$ = this.visibilityChange$.pipe(
    map((x) => document.visibilityState === 'visible'),
    distinctUntilChanged()
  );
  isAutoRefreshEnabled = false;
  isAlternateAdLoading = false;
  constructor(
    private readonly appRouterDataService: AppRouterDataService,
    private readonly featureFlagService: FeatureFlagService,
    private readonly appService: AppService,
    private readonly googleTagService: GoogleTagService
  ) {}

  ngOnInit() {
    this.adObj = GoogleAdsList[GoogleAdTypes[this.type]];
    if(this.isBetaFeatures) {
      this.adObj.sizes = SideBarLeftAdditonalSizes;
    }
    this.isAlternateAdLoading = this.featureFlagService.isFeatureEnabled(UnleashFlags.AlternateAdLoading);
    if (this.isAlternateAdLoading) {
        this.googleTagService.defineSlot(this.adObj);
    } 
    this.featureFlagService.onUpdate(() => {
      const flagStatus = this.featureFlagService.isFeatureEnabled(UnleashFlags.AlternateAdLoading);
      if (flagStatus !== this.isAlternateAdLoading) {
        this.isAlternateAdLoading = flagStatus;
      } 
      if (this.isAlternateAdLoading) {
        this.googleTagService.defineSlot(this.adObj);
      } else {
        this.appService
          .waitForConsentOnce$()
          .pipe(delay(50))
          .subscribe(() => {
            this.initAd();
          });
      }
    });
    this.subs.add(
      this.appRouterDataService.onTabChange.subscribe(() => {
        this.refreshAd();
      })
    );
     this.isAutoRefreshEnabled = this.featureFlagService.isFeatureEnabled(UnleashFlags.autoRefresh);
    if (this.isAutoRefreshEnabled) {
        this.initAdRefresh();
    } 
    this.featureFlagService.onUpdate(() => {
      const flagStatus = this.featureFlagService.isFeatureEnabled(UnleashFlags.autoRefresh);
      if (flagStatus !== this.isAutoRefreshEnabled) {
        this.isAutoRefreshEnabled = flagStatus;
        this.initAdRefresh();
      }
    });
  }

  initAdRefresh() {
    let rate = this.featureFlagService.getVariantPayloadValue(UnleashFlags.autoRefresh) || 0;
    rate = Number(rate);
    if (rate !== 0) {
      this.subs.add(
        interval(1000 * rate)
          .pipe(withLatestFrom(this.isVisible$))
          .subscribe(([_, isVisible]) => {
            if (isVisible) {
              if(this.isAlternateAdLoading) {
                if(this.googleTagService.servicesEnabled)
                this.refreshAd();
              } else {
                this.refreshAd();
              }
            }
          })
      );
    }
  }

  getAdvertisementId(): string {
    return this.adObj?.id;
  }

  initAd() {
    googletag?.cmd?.push(() => {
      const mapping = googletag.sizeMapping();
      for (const { viewPortSize, adSize } of this.adObj.sizes) {
        mapping.addSize(viewPortSize, adSize);
      }
      const builtMapping = mapping.build();
      this.adSlot = googletag
        .defineSlot(`/23323315717/${this.adObj.name}`, this.adObj.resolution, this.adObj.id)
        .defineSizeMapping(builtMapping)
        .addService(googletag.pubads());
      setTimeout(() => {
        googletag.display(this.adObj.id);
      }, 500);
    });
  }

  refreshAd() {
    if (this.isAlternateAdLoading) {
      this.googleTagService.refreshSlot(this.adObj.id);
    } else {
      googletag?.cmd?.push(() => {
        googletag.pubads().refresh([this.adSlot]);
      });
    }
  }

  ngOnDestroy(): void {
    if (this.isAlternateAdLoading) {
      this.googleTagService.destroySlot(this.adObj.id);
    } else {
      googletag?.cmd?.push(() => {
        googletag?.destroySlots([this.adSlot]);
      });
    }

    this.subs.unsubscribe();
  }
}
